import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { recordAssent } from '../services/api';
import { logout } from '../services/authService';
import RegulationCitation from './RegulationCitation';
import { REGULATION_CITATIONS } from '../data/regulationCitations';
import {
    useAssentDetail, ASSENT_VERSION, ASSENT_RECORDED_EVENT, assentBeltKey,
} from '../hooks/useAssentStatus';

// The click-through agreement (compliance fix 2, 2026-08-16). Until now the
// policy claimed a 13+ audience with no mechanism behind the claim, and the
// Terms had no assent moment to form against. This modal is that mechanism:
// one screen, once per policy version, over every signed-in page.
//
// Design decisions, each deliberate:
// - The record of truth is the backend doc (users/{email}/meta/assent),
//   written against the VERIFIED token identity. localStorage is only a belt
//   so returning users don't pay a fetch-flash on every load — clearing it
//   re-asks the backend, not the student.
// - Version-keyed: bumping ASSENT_VERSION re-asks everyone once. A re-assent
//   replaces the old record (whole-doc set on the backend).
// - "I'm under 13" signs the student out via the one shared logout path —
//   we don't collect a birthdate (data minimization; an age we stored would
//   itself be covered information), we take the attestation.
// - Fail open on network errors: a student who already assented must never
//   be locked out of their account by our 500. The modal only renders on a
//   definitive "no record" answer.
// The version, the belt and the status read now live in hooks/useAssentStatus
// so the surfaces that must WAIT for assent share one answer with the gate
// itself (#1750 — Astra was greeting students behind the unaccepted modal).
// ASSENT_VERSION is re-exported here because callers and tests import it from
// this module; the obligation to bump it on any material Terms/Privacy change
// travels with it.
export { ASSENT_VERSION };

// A returning student with an older agreement on file is told the Terms
// were updated and pointed to them (#4740, founder 2026-10-18: "Just say we
// have updated our terms and point them to read.. no need to articulate what
// changed").
export const ASSENT_CHANGE_TITLE = "We've updated our Terms";
export const ASSENT_CHANGE_LINE = 'Please take a moment to read them.';

export default function AssentGate() {
    const { currentUser } = useAuth();
    const email = currentUser?.email;
    const { status, priorVersion } = useAssentDetail(email);
    const [dismissed, setDismissed] = useState(false);
    const [saving, setSaving] = useState(false);
    const [failed, setFailed] = useState(false);

    // Reset on identity change. Today logout() does a full page load, so a
    // second account can't reach a mounted gate carrying the first account's
    // dismissal — but that is an accident of the logout implementation, not a
    // guarantee this component should depend on. Without this, changing that
    // one line elsewhere would silently skip the attestation for the next
    // student (reviewer-caught).
    useEffect(() => { setDismissed(false); setFailed(false); }, [email]);

    if (!email || status !== 'needed' || dismissed) return null;

    const agree = async () => {
        setSaving(true);
        setFailed(false);
        const res = await recordAssent(ASSENT_VERSION);
        if (res?.success === true) {
            try { localStorage.setItem(assentBeltKey(email), 'true'); } catch { /* belt only */ }
            // Tell the rest of the tree, so the surfaces that were waiting on
            // this appear immediately rather than on the next navigation.
            window.dispatchEvent(new Event(ASSENT_RECORDED_EVENT));
            setDismissed(true);
        } else {
            setFailed(true);
        }
        setSaving(false);
    };

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 px-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="assent-title"
        >
            <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl sm:p-8">
                <h2 id="assent-title" className="font-serif text-xl font-bold text-gray-900">
                    {priorVersion ? ASSENT_CHANGE_TITLE : 'Before you continue'}
                </h2>
                {/* A re-ask (an older agreement on file) points to the updated
                    Terms. It is an overlay over the page, never a navigation:
                    whatever the student had open stays as it was under it. */}
                {priorVersion && (
                    <p className="mt-3 text-[15px] leading-relaxed text-gray-700" data-testid="assent-what-changed">
                        {ASSENT_CHANGE_LINE}{' '}
                        <Link to="/terms" className="font-semibold text-[#1A4D2E] underline" target="_blank">
                            Read the Terms
                        </Link>
                    </p>
                )}
                <p className="mt-3 text-[15px] leading-relaxed text-gray-700">
                    {priorVersion ? 'To keep using Stratia' : 'To use Stratia'}, please confirm that you agree to our{' '}
                    <Link to="/terms" className="font-semibold text-[#1A4D2E] underline" target="_blank">
                        Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link to="/privacy" className="font-semibold text-[#1A4D2E] underline" target="_blank">
                        Privacy Policy
                    </Link>
                    , and that you are 13 or older.
                </p>
                <RegulationCitation
                    refs={REGULATION_CITATIONS.thirteenPlus}
                    className="mt-2 block"
                    surface="assent-gate"
                />
                {failed && (
                    <p className="mt-3 text-sm text-red-700" role="alert">
                        That didn't go through - please try again in a moment.
                    </p>
                )}
                <div className="mt-6 flex flex-col gap-2.5">
                    <button
                        type="button"
                        onClick={agree}
                        disabled={saving}
                        data-testid="assent-agree"
                        className="w-full rounded-full bg-[#1A4D2E] px-6 py-3 text-[15px] font-semibold text-white transition-colors hover:bg-[#153E25] disabled:opacity-60"
                    >
                        {saving ? 'Saving...' : 'I agree, and I am 13 or older'}
                    </button>
                    {/* A re-ask gives an adult who does not accept the new Terms
                        a way out that is not the under-13 claim (security review,
                        #4727): saying no is not saying you are a child. */}
                    {priorVersion && (
                        <button
                            type="button"
                            onClick={() => logout({ redirect: true })}
                            data-testid="assent-decline"
                            className="w-full rounded-full px-6 py-2.5 text-sm font-medium text-gray-500 transition-colors hover:text-gray-700"
                        >
                            I don't agree - sign me out
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => logout({ redirect: true })}
                        data-testid="assent-under-13"
                        className="w-full rounded-full px-6 py-2.5 text-sm font-medium text-gray-500 transition-colors hover:text-gray-700"
                    >
                        I'm under 13 - sign me out
                    </button>
                </div>
            </div>
        </div>
    );
}
